import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoIcon from "../../assets/svg/logoIcon";
import { useAuth } from "../../context/authContext";
import createAxiosInstance from "../../utils/axiosConfig";
import { add_governance_data, add_vendor_data } from "../../utils/endpoints";
import { validate } from "../../utils/formValidate";
import Alert from "../common/alerts/alert";
import BackButton from "../common/backButton";
import ChatHistory from "../common/chatHistory";
import Footer from "../common/footer";

const AddData = ({ isSidebarOpen, setIsSidebarOpen }: { isSidebarOpen: boolean, setIsSidebarOpen: (open: boolean) => void }) => {
	const navigate = useNavigate();
	const { userData, logout } = useAuth();
	const [option, setOption] = useState(0);
	const [isFadingIn, setIsFadingIn] = useState(false);
	const [, setIsFadingOut] = useState(false);
	const [errors, setErrors] = useState<any>({});
	const [loading, setLoading] = useState(false);
	const [responseError, setResponseError] = useState("");
	const [alertVisible, setAlertVisible] = useState(false);

	const [vendorCategory, setVendorCategory] = useState("");

	const [formDataVendor, setFormDataVendor] = useState({
		vendor_name: "",
		product_name: "",
		vendor_category: "",
		deliveryModel: "",
		serviceType: "",
		positive_experiences: "",
		negative_experiences: "",
		advice_for_prospective_customers: "",
		third_party_rating: "",
		other_category: "",
	});

	const [spellingErrors, setSpellingErrors] = useState<any>(null);
	const [showSpellingModal, setShowSpellingModal] = useState(false);
	const [ignoreSpelling, setIgnoreSpelling] = useState(false);

	const [formDataGovernance, setFormDataGovernance] = useState({
		question: "",
		answer: "",
		persona: "",
		maturityLevel: "",
	});

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => {
		const { name, value } = e.target;
		setFormDataVendor({ ...formDataVendor, [name]: value });
		validate({ [name]: value }, setErrors);

		if (name === "vendor_category") {
			setVendorCategory(value);
		}
	};

	const handleGovernanceChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => {
		const { name, value } = e.target;
		setFormDataGovernance({ ...formDataGovernance, [name]: value });
		validate({ [name]: value }, setErrors);
	};

	const handleGovernanceSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		if (!validate(formDataGovernance, setErrors)) {
			console.log(errors);
			setLoading(false);
			return;
		}

		const payload = {
			user_id: userData?.id,
			question: formDataGovernance.question,
			answer: formDataGovernance.answer,
			persona: formDataGovernance.persona,
			maturityLevel: formDataGovernance.maturityLevel,
		};

		if (!ignoreSpelling) {
			const spellingResults = await checkSpellings(payload);
			if (spellingResults && Object.keys(spellingResults).length > 0) {
				setSpellingErrors(spellingResults);
				setShowSpellingModal(true);
				setLoading(false);
				return;
			}
		}

		try {
			const axiosInstance = createAxiosInstance();
			const response = await axiosInstance.post(add_governance_data, payload);
			if(response.status == 400 || response.status == 401){
				logout();
				window.location.href = '/';
			}
			if (response.data?.user?.error) {
				setResponseError(response?.data?.user?.error);
				setAlertVisible(true);
			} else {
				setResponseError("");
				setAlertVisible(true);
				setFormDataGovernance({
					question: "",
					answer: "",
					persona: "",
					maturityLevel: "",
				});
				setErrors({});
				setSpellingErrors({});
				setIgnoreSpelling(false);
			}
		} catch (e: any) {
			if(e.response.status == 400 || e.response.status == 401){
				logout();
				window.location.href = '/';
			}
			console.log(e, "error");
			setResponseError("Some errors occured. Please try again later.");
			setAlertVisible(true);
		}finally{
			setLoading(false);
		}
	};

	const handleOptionSelect = (option: number) => {
		setIsFadingOut(true);
		setOption(option);
		setTimeout(() => {
			setIsFadingIn(true);
		}, 1000);
	};

	// Add new function to check spellings
	const checkSpellings = async (payload: any) => {
		try {
			const axiosInstance = createAxiosInstance();
			const response = await axiosInstance.post(
				"/upload/checkSpellings",
				payload
			);
			if(response.status == 400 || response.status == 401){
				logout();
				window.location.href = '/';
			}
			return response.data;
		} catch (error) {
			console.error("Error checking spellings:", error);
			return null;
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const { other_category, ...formDataForValidation } = formDataVendor;
		if (!validate(formDataForValidation, setErrors)) {
			console.log(errors);
			setLoading(false);
			return;
		}

		setLoading(true);
		const payload = {
			user_id: userData?.id,
			vendorName: formDataVendor.vendor_name,
			productName: formDataVendor.product_name,
			vendorCategory: formDataVendor.vendor_category,
			deliveryModel: formDataVendor.deliveryModel,
			serviceType: formDataVendor.serviceType,
			positiveExperience: formDataVendor.positive_experiences,
			negativeExperience: formDataVendor.negative_experiences,
			advice: formDataVendor.advice_for_prospective_customers,
			rating: formDataVendor.third_party_rating,
			other: formDataVendor.other_category,
		};
		// Check spellings if not ignored
		if (!ignoreSpelling) {
			const spellingResults = await checkSpellings(payload);
			if (spellingResults && Object.keys(spellingResults).length > 0) {
				setSpellingErrors(spellingResults);
				setShowSpellingModal(true);
				setLoading(false);
				return;
			}
		}

		try {
			const axiosInstance = createAxiosInstance();
			const response = await axiosInstance.post(add_vendor_data, payload);
			if(response.status == 400 || response.status == 401){
				logout();
				window.location.href = '/';
			}

			if (response.data?.user?.error) {
				setResponseError(response?.data?.user?.error);
				setAlertVisible(true);
			} else {
				setResponseError("");
				setAlertVisible(true);
				setFormDataVendor({
					vendor_name: "",
					product_name: "",
					vendor_category: "",
					deliveryModel: "",
					serviceType: "",
					positive_experiences: "",
					negative_experiences: "",
					advice_for_prospective_customers: "",
					third_party_rating: "",
					other_category: "",
				});
				setVendorCategory("");
				setErrors({});
				setSpellingErrors({});
				setIgnoreSpelling(false);
			}
		} catch (e: any) {
			console.log(e, "error");
			if(e.response.status == 400 || e.response.status == 401){
				logout();
				window.location.href = '/';
			}
			setResponseError("Some errors occured. Please try again later.");
			setAlertVisible(true);
		} finally {
			setLoading(false);
		}
	};

	const SpellingModal = ({ show, onHide, onIgnore }: any) => {
		return (
			<div
				className={`modal ${show ? "d-block" : ""}`}
				style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
			>
				<div className="modal-dialog">
					<div className="modal-content">
						<div className="modal-header" style={{ border: "none" }}>
							<h5 className="modal-title">Spelling Mistakes Found</h5>
							<button
								type="button"
								className="btn-close"
								onClick={onHide}
							></button>
						</div>
						<div className="modal-body">
							<p>Please check the highlighted fields for spelling mistakes.</p>
						</div>
						<div className="modal-footer d-flex" style={{ border: "none" }}>
							<button
								type="button"
								className="btn btn-primary"
								onClick={onHide}
								style={{
									background: '#5376F0',
									color: 'white',
									border: '1px solid #5376F0',
									borderRadius: "1.25rem",
									padding: "0.5rem 2.5rem"
								}}
							>
								Fix Typos
							</button>
							<button
								type="button"
								className="btn btn-light"
								onClick={onIgnore}
								style={{
									background: '#26397E',
									color: 'white',
									border: '1px solid #26397E',
									borderRadius: "1.25rem",
									padding: "0.5rem 2.5rem"
								}}
							>
								Ignore and submit
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (ignoreSpelling) {
			if (option == 1) {
				handleSubmit(new Event("submit") as any);
			} else {
				handleGovernanceSubmit(new Event("submit") as any);
			}
		}
	}, [ignoreSpelling]);

	// const openSidebarButton = (
	// 	<div className="d-flex justify-content-center align-items-center mt-5">
	// 		<button className="btn btn-primary" onClick={() => setIsSidebarOpen(true)}>
	// 			Add Data
	// 		</button>
	// 	</div>
	// );

	const sidebar = (
		<>
			{isSidebarOpen && (
				<>
					<div
						className="offcanvas-backdrop fade show"
						onClick={() => setIsSidebarOpen(false)}
					></div>
					<div
						className="offcanvas offcanvas-end show w-55"
						tabIndex={-1}
						id="offcanvasExample"
						aria-labelledby="offcanvasExample"
						style={{ zIndex: 1050 }}
					>
						<div className="offcanvas-header">
							<h5 className="offcanvas-title" id="offcanvasExample" style={{
								fontSize: '1.25rem',
								fontWeight: 'bold',
								marginLeft: '3rem',
								padding: '10px 0px'
							}}>
								{option === 1 ? "Add Vendor Selection Data" : option === 2 ? "Add Governance and Management Oversight Data" : "Add Ingestion Data"}
							</h5>
							<button
								type="button"
								className="btn-close"
								data-bs-dismiss="offcanvas"
								aria-label="Close"
								onClick={() => setIsSidebarOpen(false)}
							></button>
						</div>
						<div className="offcanvas-body scroller px-5">
							{/* --- Main AddData Form Content --- */}
							<h2 className="my-3 my-md-4">
								
								{option === 1
									? ""
									: option === 2
									? ""
									: "Select a use case:"}{" "}
							</h2>
							{/* {userData?.is_admin && (
								<p className="text-secondary">
									want to upload a file instead ?{" "}
									<a href="/uploadFiles">Upload Data</a>
								</p>
							)} */}
							{((option !== 1 && option !== 2) || !isFadingIn) && (
								<ul
									className={`${
										option !== 1 && option !== 2 ? "fade-in" : "fade-out"
									} list-group checklist`}
								>
									<li
										className="list-group-item"
										style={{
											background: "#ebe6e3",
											cursor: "pointer",
											textAlign: "left",
										}}
										onClick={() => handleOptionSelect(1)}
									>
										<input
											className="form-check-input me-1"
											type="radio"
											name="listGroupRadio"
											id="firstRadio"
											checked={option === 1}
											style={{ cursor: "pointer" }}
										/>
										<label
											className="form-check-label"
											htmlFor="firstRadio"
											style={{ cursor: "pointer" }}
										>
											I want to add data for Vendor Selection.
										</label>
									</li>
									<li
										className="list-group-item"
										style={{
											background: "#ebe6e3",
											cursor: "pointer",
											textAlign: "left",
										}}
										onClick={() => handleOptionSelect(2)}
									>
										<input
											className="form-check-input me-1"
											type="radio"
											name="listGroupRadio"
											id="secondRadio"
											checked={option === 2}
											style={{ cursor: "pointer" }}
										/>
										<label
											className="form-check-label"
											htmlFor="secondRadio"
											style={{ cursor: "pointer" }}
										>
											I want to add data for Board/Governance and Management
											Oversight.
										</label>
									</li>
								</ul>
							)}
							{option == 1 && isFadingIn && (
								<form action="" onSubmit={handleSubmit} className="fade-in">
									<p className="" style={{ color: "#003C9E", fontSize: '1.125rem', fontWeight: 'bold'}}>
										Vendor Selection:
									</p>

									<div className="d-flex flex-column flex-md-row gap-3">
										<div className="mb-3 w-100">
											<label htmlFor="floatingInput" className="form-label mb-2">Name of Vendor</label>
											<input
												type="text"
												className={`form-control bg-white form-control-lg`}
												id="floatingInput"
												// placeholder="Name of vendor"
												name="vendor_name"
												value={formDataVendor.vendor_name}
												onChange={handleChange}
											/>
											
											{errors.vendor_name && (
												<div className="text-danger small">
													{errors.vendor_name}
												</div>
											)}

											{spellingErrors?.vendorName && (
												<div className="text-danger small">
													Detected a possible spelling mistake in the Name of
													Vendor field.
												</div>
											)}
										</div>

										<div className="mb-3 w-100">
											<label htmlFor="floatingInput" className="form-label mb-2">Name of Product</label>
											<input
												type="text"
												className={`form-control bg-white form-control-lg`}
												id="floatingInput"
												// placeholder="Name of vendor"
												name="product_name"
												value={formDataVendor.product_name}
												onChange={handleChange}
											/>
											
											{errors.product_name && (
												<div className="text-danger small">
													{errors.product_name}
												</div>
											)}
											{spellingErrors?.productName && (
												<div className="text-danger small">
													Detected a possible spelling mistake in the Name of
													Product field.
												</div>
											)}
										</div>
									</div>

									<div className="mb-3 ">
										<label htmlFor="floatingSelect" className="form-label mb-2">Vendor Category</label>
										<select
											className={`form-select bg-white form-select-lg`}
											id="floatingSelect"
											aria-label="Floating label select example"
											name="vendor_category"
											value={vendorCategory}
											onChange={handleChange}
										>
											<option></option>
											<option value="Anti-malware Software">
												Anti-malware Software
											</option>
											<option value="Antivirus Systems">
												Antivirus Systems
											</option>
											<option value="Backup">Backup</option>
											<option value="Cyber Risk Quantification (CRQ)">
												Cyber Risk Quantification (CRQ)
											</option>
											<option value="Data Loss Prevention (DLP)">
												Data Loss Prevention (DLP)
											</option>
											<option value="Database and Storage Security">
												Database and Storage Security
											</option>
											<option value="Discovery and Classification">
												Discovery and Classification
											</option>
											<option value="Edge Location Security">
												Edge Location Security
											</option>
											<option value="Encryption">Encryption</option>
											<option value="Encryption of Cloud Data at Rest, in Motion, and in Use">
												Encryption of Cloud Data at Rest, in Motion, and in Use
											</option>
											<option value="Endpoint Detection and Response (EDR)">
												Endpoint Detection and Response (EDR)
											</option>
											<option value="Enterprise Mobility Management">
												Enterprise Mobility Management
											</option>
											<option value="Enterprise Mobility Management (EMM)">
												Enterprise Mobility Management (EMM)
											</option>
											<option value="Firewalls">Firewalls</option>
											<option value="Governance, Risk, and Compliance (GRC)">
												Governance, Risk, and Compliance (GRC)
											</option>
											<option value="Identity and Access Management (IAM)">
												Identity and Access Management (IAM)
											</option>
											<option value="Infrastructure Security">
												Infrastructure Security
											</option>
											<option value="Intrusion Detection and Prevention System (IDPS)">
												Intrusion Detection and Prevention System (IDPS)
											</option>
											<option value="Mobile Application Management (MAM)">
												Mobile Application Management (MAM)
											</option>
											<option value="Multifactor Authentication (MFA)">
												Multifactor Authentication (MFA)
											</option>
											<option value="Network Access Control (NAC)">
												Network Access Control (NAC)
											</option>
											<option value="Network Detection and Response (NDR)">
												Network Detection and Response (NDR)
											</option>
											<option value="Networking Security">
												Networking Security
											</option>
											<option value="Next-generation Firewall (NGFW)">
												Next-generation Firewall (NGFW)
											</option>
											<option value="Secure Access Service Edge (SASE)">
												Secure Access Service Edge (SASE)
											</option>
											<option value="Secure Email Gateways (SEG)">
												Secure Email Gateways (SEG)
											</option>
											<option value="Security Information and Event Management (SIEM)">
												Security Information and Event Management (SIEM)
											</option>
											<option value="Security Orchestration, Automation, and Response (SOAR)">
												Security Orchestration, Automation, and Response (SOAR)
											</option>
											<option value="Third Party Risk Management (TPRM)">
												Third Party Risk Management (TPRM)
											</option>
											<option value="User and Entity Behavior Analytics (UEBA)">
												User and Entity Behavior Analytics (UEBA)
											</option>
											<option value="Virtual Private Networks (VPNs)">
												Virtual Private Networks (VPNs)
											</option>
											<option value="Web Application Firewalls (WAFs)">
												Web Application Firewalls (WAFs)
											</option>
											<option value="Other">Other</option>
										</select>
										
										{errors.vendor_category && (
											<div className="text-danger small">
												{errors.vendor_category}
											</div>
										)}
									</div>

									{vendorCategory === "Other" && (
										<div className="mb-3">
											<label htmlFor="otherCategory">
												Specify Other Category
											</label>
											<input
												type="text"
												className="form-control"
												id="otherCategory"
												placeholder="Specify other category"
												name="other_category"
												value={formDataVendor.other_category}
												onChange={handleChange}
											/>
											{errors.other_category && (
												<div className="text-danger small">
													{errors.other_category}
												</div>
											)}
										</div>
									)}
									<div className="mb-5 mt-4">
										<span className="form-label mb-4" style={{ textAlign: "left" }}>
											Delivery Model:
										</span>
										<div className="d-flex align-items-center justify-content-start gap-3 mb-3 mt-3">
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.deliveryModel === "Cloud"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "deliveryModel", value: "Cloud" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Cloud
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.deliveryModel === "On-Premise"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "deliveryModel", value: "On-Premise" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												On-Premise
											</button>
										</div>
										{errors.deliveryModel && (
											<div className="text-danger small">
												{errors.deliveryModel}
											</div>
										)}
									</div>

									<div className="form-floating mb-5 mt-4">
										<span className="form-label mb-4" style={{ textAlign: "left" }}>
											Select the type of service/solution:
										</span>
										<div className="d-flex flex-column flex-wrap flex-md-row align-items-start justify-content-start gap-3 mb-3 mt-3">
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.serviceType === "Product (Installed)"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "serviceType", value: "Product (Installed)" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Product (installed)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.serviceType === "Service (Consumed)"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "serviceType", value: "Service (Consumed)" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Service (consumed)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.serviceType === "Professional Services (Installed / Supported)"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "serviceType", value: "Professional Services (Installed / Supported)" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Professional Services (Installed / Supported)
											</button>
											<button
												type="button"
												className={`btn btn-option large-padding ${
													formDataVendor.serviceType === "Reseller / VAR (acquired / renewed)"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "serviceType", value: "Reseller / VAR (acquired / renewed)" }
												} as React.ChangeEvent<HTMLSelectElement>)}
												
											>
												Reseller / VAR (Acquired / Renewed)
											</button>
											<button
												type="button"
												className={`btn btn-option large-padding ${
													formDataVendor.serviceType === "Training (Provided / Consumed)"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "serviceType", value: "Training (Provided / Consumed)" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Training (Provided / Consumed)
											</button>
										</div>
										{errors.serviceType && (
											<div className="text-danger small">
												{errors.serviceType}
											</div>
										)}
									</div>

									<div className="d-flex flex-column flex-md-row gap-3">
										<div className="mb-3 w-100">
											<label
												htmlFor="positive_experiences text-wrap"
												style={{ whiteSpace: "wrap", textOverflow: "ellipsis" }}
												className="form-label mb-2"
											>
												What were three Positive experiences?
											</label>
											<input
												type="text"
												className="form-control bg-white form-control-lg"
												id="positive_experiences"
												// placeholder="Specify other category"
												name="positive_experiences"
												value={formDataVendor.positive_experiences}
												onChange={handleChange}
											/>
											
											{errors.positive_experiences && (
												<div className="text-danger small">
													{errors.positive_experiences}
												</div>
											)}

											{spellingErrors?.positiveExperience && (
												<div className="text-danger small">
													Detected a possible spelling mistake in the Positive
													experiences field.
												</div>
											)}
										</div>

										<div className="mb-3 w-100">
											<label
												htmlFor="negative_experiences"
												className="form-label mb-2"
												style={{ whiteSpace: "wrap", textOverflow: "ellipsis" }}
											>
												What were three Negative experiences?
											</label>
											<input
												type="text"
												className="form-control bg-white form-control-lg"
												id="negative_experiences"
												// placeholder="Specify other category"
												name="negative_experiences"
												value={formDataVendor.negative_experiences}
												onChange={handleChange}
											/>
											{errors.negative_experiences && (
												<div className="text-danger small">
													{errors.negative_experiences}
												</div>
											)}
											{spellingErrors?.negativeExperience && (
												<div className="text-danger small">
													Detected a possible spelling mistake in the Negative
													experiences field.
												</div>
											)}
										</div>
									</div>
									<div className="mb-3">
										<label
											className="form-label mb-2"
											style={{ whiteSpace: "wrap", textOverflow: "ellipsis" }}
											htmlFor="advice_for_prospective_customers"
										>
											What advice do you have for prospective customers before
											they buy?
										</label>
										<input
											type="text"
											className="form-control bg-white form-control-lg"
											id="advice_for_prospective_customers"
											// placeholder="Specify other category"
											name="advice_for_prospective_customers"
											value={formDataVendor.advice_for_prospective_customers}
											onChange={handleChange}
										/>
										{errors.advice_for_prospective_customers && (
											<div className="text-danger small">
												{errors.advice_for_prospective_customers}
											</div>
										)}
										{spellingErrors?.advice && (
											<div className="text-danger small">
												Detected a possible spelling mistake in the advice
												field.
											</div>
										)}
									</div>

									<div className="mb-5 mt-4">
										<span className="form-label mb-4" style={{ textAlign: "left" }}>
											Overall vendor / third party rating:
										</span>
										<div className="d-flex flex-column flex-md-row align-items-start justify-content-start gap-3 mb-3 mt-3">
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.third_party_rating === "1"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "third_party_rating", value: "1" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												1 (Avoid)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.third_party_rating === "2"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "third_party_rating", value: "2" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												2 (Below average)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.third_party_rating === "3"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "third_party_rating", value: "3" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												3 (Neutral)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.third_party_rating === "4"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "third_party_rating", value: "4" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												4 (Above average)
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataVendor.third_party_rating === "5"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleChange({
													target: { name: "third_party_rating", value: "5" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												5 (Outstanding)
											</button>
										</div>
										{errors.third_party_rating && (
											<div className="text-danger small">
												{errors.third_party_rating}
											</div>
										)}
									</div>
									<br />
									<div className="mb-3 d-flex justify-content-end">
										<button
											type="submit"
											className="btn btn-rounded w-55"
											onClick={handleSubmit}
											disabled={loading}
										>
											{loading ? <div
												className="d-flex justify-content-center align-items-center"
												style={{ height: "20px", color: 'white' }}
											>
												<div className="spinner-border text-white" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
											</div> : "SUBMIT"}
										</button>
									</div>
								</form>
							)}

							{option == 2 && isFadingIn && (
								<form
									action=""
									onSubmit={handleGovernanceSubmit}
									className="fade-in"
								>
									<p className="" style={{ color: "#003C9E", fontSize: '1.125rem', fontWeight: 'bold'}}>
										Governance and Management Oversight:
									</p>

									<div className="mb-3 ">
										<label htmlFor="floatingSelect" className="form-label mb-2">Persona:</label>
										<select
											className={`form-select bg-white form-control-lg`}
											id="floatingSelect"
											aria-label="Floating label select example"
											name="persona"
											value={formDataGovernance.persona}
											onChange={handleGovernanceChange}
										>
											<option></option>
											<option value="Director">Director</option>
											<option value="Executive">Executive</option>
											<option value="CEO">CEO</option>
											<option value="CFO">CFO</option>
											<option value="Auditor">Auditor</option>
										</select>
										
										{errors.persona && (
											<div className="text-danger small">{errors.persona}</div>
										)}
									</div>

									<div className="mb-3">
										<label htmlFor="question" className="form-label mb-2">User Question:</label>
										<input
											type="text"
											className="form-control bg-white form-control-lg"
											id="question"
											// placeholder="Type your question here..."
											name="question"
											value={formDataGovernance.question}
											onChange={handleGovernanceChange}
										/>
										
										{errors.question && (
											<div className="text-danger small">{errors.question}</div>
										)}
										{spellingErrors?.question && (
											<div className="text-danger small">
												Detected a possible spelling mistake in the User
												Question field.
											</div>
										)}
									</div>

									<div className=" mb-3">
										<label htmlFor="answer" className="form-label mb-2">System Answer:</label>
										<input
											type="text"
											className="form-control bg-white form-control-lg"
											id="answer"
											// placeholder="Type answer to the question here..."
											name="answer"
											value={formDataGovernance.answer}
											onChange={handleGovernanceChange}
										/>
										{errors.answer && (
											<div className="text-danger small">{errors.answer}</div>
										)}
										{spellingErrors?.answer && (
											<div className="text-danger small">
												Detected a possible spelling mistake in the System
												Answer field.
											</div>
										)}
									</div>

									<div className="mb-4 mt-4">
										<span className="form-label mb-2" style={{ textAlign: "left" }}>
											Familiarity with Information security processes:
										</span>
										<div className="d-flex flex-column flex-md-row align-items-start justify-content-start gap-3 mb-4 mt-3">
											<button
												type="button"
												className={`btn btn-option ${
													formDataGovernance.maturityLevel === "Novice"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleGovernanceChange({
													target: { name: "maturityLevel", value: "Novice" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Novice
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataGovernance.maturityLevel === "Intermediate"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleGovernanceChange({
													target: { name: "maturityLevel", value: "Intermediate" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Intermediate
											</button>
											<button
												type="button"
												className={`btn btn-option ${
													formDataGovernance.maturityLevel === "Advanced"
														? "selected-option"
														: "unselected-option"
												}`}
												onClick={() => handleGovernanceChange({
													target: { name: "maturityLevel", value: "Advanced" }
												} as React.ChangeEvent<HTMLSelectElement>)}
											>
												Advanced
											</button>
										</div>
										{errors.maturityLevel && (
											<div className="text-danger small">
												{errors.maturityLevel}
											</div>
										)}
									</div>
									<br />
									<div className="mb-3 mt-4 d-flex justify-content-end">
										<button
											type="submit"
											className="btn btn-rounded w-55"
											onClick={handleGovernanceSubmit}
											disabled={loading}
										>
											{loading ? <div
												className="d-flex justify-content-center align-items-center"
												style={{ height: "20px", color: 'white' }}
											>
												<div className="spinner-border text-white" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
											</div> : "SUBMIT"}
										</button>
									</div>
								</form>
							)}
							<div className="mb-3">
								<br />
								{option >= 1 ? (
									<button
										className="btn btn-link w-100"
										onClick={() => {
											handleOptionSelect(0);
										}}
									>
										{" "}
										<i className="bi bi-arrow-left me-2"></i> Back
									</button>
								) : null}
							</div>
						</div>
					</div>
				</>
			)}
		</>
	);

	if (!isSidebarOpen){
		return null
	}
	return (
		<section className="">
			<div className="row align-items-center justify-content-center w-100">
				<div className="col-md-10 col-lg-10 col-xl-7">
					{/* {!isSidebarOpen && openSidebarButton} */}
					{sidebar}
				</div>
				{/* <Footer /> */}
				{/* <ChatHistory
					onNavigate={(path: string) => {
						navigate(path);
					}}
				/> */}
			</div>
			<Alert
				message={`${
					responseError.length > 1 ? responseError : "Data added successfully."
				}`}
				type={`${responseError.length > 1 ? "danger" : "success"}`}
				show={alertVisible}
				onClose={() => setAlertVisible(false)}
			/>
			{showSpellingModal && <SpellingModal
				show={showSpellingModal}
				onHide={() => {
					setShowSpellingModal(false);
				}}
				onIgnore={() => {
					setIgnoreSpelling(true);
					setShowSpellingModal(false);
				}}
			/>}
		</section>
	);
};

export default AddData;
